import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowLeft, LogOut, Trash2, Check, X, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "blendz_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const fetchBookings = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${token}` } });
      setBookings(res.data || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("Session expired. Sign in again.");
      } else {
        toast.error("Failed to load bookings.");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const tk = res.data.token;
      localStorage.setItem(TOKEN_KEY, tk);
      setToken(tk);
      setPassword("");
      toast.success("Welcome back, Jay.");
    } catch {
      toast.error("Wrong password.");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setBookings([]);
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/bookings/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success(`Marked ${status}.`);
    } catch {
      toast.error("Update failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await axios.delete(`${API}/bookings/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setBookings((bs) => bs.filter((b) => b.id !== id));
      toast.success("Deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-md bg-[#121212] border border-[#27272A] rounded-md p-10">
          <Link to="/" className="text-xs uppercase tracking-[0.3em] text-dim hover:text-white inline-flex items-center gap-2 mb-8" data-testid="admin-back-link">
            <ArrowLeft size={14} /> Back to site
          </Link>
          <h1 className="font-display text-4xl text-white mb-2">ADMIN ACCESS</h1>
          <p className="text-dim text-sm mb-8">Blendz.Jay control room.</p>
          <Label className="text-xs uppercase tracking-[0.25em] text-dim mb-2 block">Password</Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            className="bg-[#0A0A0A] border-[#27272A] h-12 focus-visible:ring-accent mb-6"
            placeholder="Enter admin password"
          />
          <Button type="submit" data-testid="admin-login-button" className="w-full h-12 bg-accent hover:bg-[#D90429] text-white font-bold uppercase tracking-[0.25em]">
            Sign In
          </Button>
        </form>
      </div>
    );
  }

  const visible = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);
  const counts = {
    all: bookings.length,
    pending: bookings.filter((b) => b.status === "pending").length,
    confirmed: bookings.filter((b) => b.status === "confirmed").length,
    cancelled: bookings.filter((b) => b.status === "cancelled").length,
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-[#27272A] bg-[#0A0A0A]">
        <div className="container-x py-6 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.4em] text-accent">Control Room</div>
            <h1 className="font-display text-3xl">BOOKINGS</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="btn-outline" data-testid="admin-site-link">View Site</Link>
            <Button onClick={logout} variant="outline" data-testid="admin-logout-button" className="border-[#27272A]">
              <LogOut size={14} className="mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container-x py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total" value={counts.all} active={filter === "all"} onClick={() => setFilter("all")} testId="filter-all" />
          <StatCard label="Pending" value={counts.pending} active={filter === "pending"} onClick={() => setFilter("pending")} accent testId="filter-pending" />
          <StatCard label="Confirmed" value={counts.confirmed} active={filter === "confirmed"} onClick={() => setFilter("confirmed")} testId="filter-confirmed" />
          <StatCard label="Cancelled" value={counts.cancelled} active={filter === "cancelled"} onClick={() => setFilter("cancelled")} testId="filter-cancelled" />
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="text-xs uppercase tracking-[0.3em] text-dim">{visible.length} result{visible.length !== 1 ? "s" : ""}</div>
          <Button onClick={fetchBookings} variant="outline" className="border-[#27272A]" data-testid="admin-refresh-button">
            <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        <div className="bg-[#121212] border border-[#27272A] rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-[#27272A] hover:bg-transparent">
                <TableHead className="text-dim uppercase text-[10px] tracking-[0.3em]">Client</TableHead>
                <TableHead className="text-dim uppercase text-[10px] tracking-[0.3em]">Phone</TableHead>
                <TableHead className="text-dim uppercase text-[10px] tracking-[0.3em]">Service</TableHead>
                <TableHead className="text-dim uppercase text-[10px] tracking-[0.3em]">Date / Time</TableHead>
                <TableHead className="text-dim uppercase text-[10px] tracking-[0.3em]">Notes</TableHead>
                <TableHead className="text-dim uppercase text-[10px] tracking-[0.3em]">Status</TableHead>
                <TableHead className="text-dim uppercase text-[10px] tracking-[0.3em] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.length === 0 && (
                <TableRow className="border-[#27272A]">
                  <TableCell colSpan={7} className="text-center py-12 text-dim" data-testid="admin-empty-state">
                    No bookings yet.
                  </TableCell>
                </TableRow>
              )}
              {visible.map((b) => (
                <TableRow key={b.id} className="border-[#27272A] hover:bg-[#0A0A0A]" data-testid={`booking-row-${b.id}`}>
                  <TableCell className="font-semibold text-white">{b.name}</TableCell>
                  <TableCell><a href={`tel:${b.phone}`} className="text-white/80 hover:text-accent">{b.phone}</a></TableCell>
                  <TableCell className="text-white/80">{b.service}</TableCell>
                  <TableCell className="text-white/80 whitespace-nowrap">{b.date} · {b.time}</TableCell>
                  <TableCell className="text-dim text-sm max-w-[200px] truncate">{b.notes || "—"}</TableCell>
                  <TableCell>
                    <StatusBadge status={b.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-2">
                      <Select value={b.status} onValueChange={(v) => setStatus(b.id, v)}>
                        <SelectTrigger className="w-[130px] h-9 bg-[#0A0A0A] border-[#27272A]" data-testid={`status-select-${b.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121212] border-[#27272A] text-white">
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        size="icon"
                        variant="outline"
                        className="border-[#27272A] hover:border-accent hover:text-accent h-9 w-9"
                        onClick={() => remove(b.id)}
                        data-testid={`delete-button-${b.id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

const StatCard = ({ label, value, active, accent, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`text-left p-5 rounded-md border transition-colors ${
      active ? "border-accent bg-accent/5" : "border-[#27272A] bg-[#121212] hover:border-white/30"
    }`}
  >
    <div className={`text-[10px] uppercase tracking-[0.3em] mb-2 ${accent ? "text-accent" : "text-dim"}`}>{label}</div>
    <div className="font-display text-4xl text-white">{value}</div>
  </button>
);

const StatusBadge = ({ status }) => {
  const map = {
    pending: { label: "Pending", className: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30" },
    confirmed: { label: "Confirmed", className: "bg-green-500/15 text-green-400 border-green-500/30" },
    cancelled: { label: "Cancelled", className: "bg-red-500/15 text-red-400 border-red-500/30" },
  };
  const s = map[status] || map.pending;
  return <Badge variant="outline" className={`uppercase tracking-[0.2em] text-[10px] ${s.className}`}>{s.label}</Badge>;
};
